import type { ApolloCache } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import type { JsonSchema7, VerticalLayout } from '@jsonforms/core';
import {
  addFieldToCustomAttributeSchema,
  removeField,
  removeFieldFromConditions,
  type TypedFormFieldConfiguration,
} from '@risksmart-app/form-configuration/src/field-service';
import {
  type CustomAttributeFieldType,
  isCustomAttributeFieldType,
  type Option,
} from '@risksmart-app/form-configuration/src/field-types/types';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { useCallback } from 'react';
import type {
  FormEditorInitialData,
  FormEditorOutput,
} from 'src/blocks/form-editor-dialog';

import { evictField } from '@/utils/graphqlUtils';

/**
 * Section/ordering tables store JSON Schema scope format (`#/properties/{path}`)
 * while form_field_configuration stores server format (`CustomAttributeData.{path}`).
 * Convert scope -> server format when writing field configuration rows.
 */
const scopeToServerId = (id: string): string =>
  id.startsWith('#/properties/')
    ? `CustomAttributeData.${id.slice('#/properties/'.length)}`
    : id;

/**
 * Converts the editor's `{ id, label }[]` option format to the
 * `Option[]` format expected by the orchestrator.
 */
const mapEditorOptionsToFieldOptions = (
  options?: { id: string; label: string }[]
): Option[] => {
  if (!options || options.length === 0) {
    return [];
  }

  return options.map((opt) =>
    opt.id !== opt.label
      ? { _tag: 'AltValueOption' as const, Value: opt.label, AltValue: opt.id }
      : { _tag: 'StringOption' as const, Value: opt.label }
  );
};

/**
 * Generates a unique attribute name for a new custom field.  Mirrors the
 * server-side format `${Date.now()}_${type}` but appends an index to avoid
 * collisions when multiple fields are created in the same millisecond
 * (the server is safe because each call is its own HTTP request, but a
 * client-side loop can collide).
 */
const generateAttributeName = (
  fieldType: CustomAttributeFieldType,
  index: number
): string => `${Date.now()}_${index}_${fieldType}`;

/**
 * Single Hasura mutation that atomically persists the entire form editor
 * output: custom attribute schema, form configuration, field configurations,
 * sections, ordering, and any deletions.  Replaces the previous three-phase
 * flow (delete-action -> insert-action -> consolidated mutation) so that a
 * mid-flight failure can never leave the DB in a partial state.
 */
const SAVE_FORM_CONFIGURATION = gql`
  mutation saveFormConfiguration(
    $parentType: parent_type_enum!
    $formConfigurationObject: form_configuration_insert_input!
    $fieldConfigurations: [form_field_configuration_insert_input!]!
    $sectionConfig: [form_section_configuration_insert_input!]!
    $sectionOrdering: [form_section_ordering_insert_input!]!
    $fieldMappings: [form_section_field_insert_input!]!
    $customAttributeSchemas: [custom_attribute_schema_insert_input!]!
    $fieldIds: [String!]!
    $sectionIds: [String!]!
    $customFieldsToDelete: [String!]!
  ) {
    insert_custom_attribute_schema(
      objects: $customAttributeSchemas
      on_conflict: {
        constraint: custom_attribute_schema_pkey
        update_columns: [Schema, UiSchema]
      }
    ) {
      affected_rows
    }

    insert_form_configuration_one(
      object: $formConfigurationObject
      on_conflict: {
        constraint: form_configuration_pkey
        update_columns: [ParentType, CustomAttributeSchemaId]
      }
    ) {
      CreatedAtTimestamp
    }

    insert_form_field_configuration(
      objects: $fieldConfigurations
      on_conflict: {
        constraint: form_field_configuration_pkey1
        update_columns: [
          Required
          ReadOnly
          Hidden
          Label
          Description
          DefaultValue
          Conditions
        ]
      }
    ) {
      returning {
        FieldId
      }
    }

    insert_form_section_configuration(
      objects: $sectionConfig
      on_conflict: {
        constraint: form_section_configuration_pkey
        update_columns: [Label, Description]
      }
    ) {
      returning {
        SectionId
        Label
      }
    }

    insert_form_section_ordering(
      objects: $sectionOrdering
      on_conflict: {
        constraint: form_section_ordering_pkey1
        update_columns: [Position]
      }
    ) {
      returning {
        SectionId
        Position
      }
    }

    insert_form_section_field(
      objects: $fieldMappings
      on_conflict: {
        constraint: form_section_field_pkey
        update_columns: [SectionId]
      }
    ) {
      returning {
        FieldId
        SectionId
      }
    }

    delete_form_field_ordering(
      where: {
        FieldId: { _nin: $fieldIds }
        FormConfigurationParentType: { _eq: $parentType }
      }
    ) {
      affected_rows
    }

    delete_form_section_configuration(
      where: {
        SectionId: { _nin: $sectionIds }
        FormConfigurationParentType: { _eq: $parentType }
      }
    ) {
      affected_rows
    }

    delete_form_field_configuration(
      where: {
        FieldId: { _in: $customFieldsToDelete }
        FormConfigurationParentType: { _eq: $parentType }
      }
    ) {
      affected_rows
    }
  }
`;

interface UseFormEditorSaveOptions {
  parentType: FormId;
  initialData: FormEditorInitialData;
  /**
   * The current custom attribute schema (or null if none exists).  Provides
   * the read-modify-write base for client-side schema computation.
   */
  customAttributeSchema: {
    Id: string;
    Schema: JsonSchema7;
    UiSchema: VerticalLayout;
  } | null;
  /**
   * The current form_field_configuration rows.  Used to recompute Conditions
   * on remaining fields when a referenced field is deleted (matches the
   * server-side orchestrator's behaviour in deleteFieldAndPersist).
   */
  formFieldConfigurations: TypedFormFieldConfiguration[] | null;
}

/**
 * Returns a save function that persists the full form editor output via a
 * single atomic Hasura mutation.
 *
 * Schema deltas (custom field add / delete) are computed client-side using
 * the shared `@risksmart-app/form-configuration` orchestrator helpers, then
 * the resulting `Schema` / `UiSchema` plus all layout, ordering, mapping and
 * field-config writes are bundled into one Hasura GraphQL mutation.  Because
 * Hasura runs all top-level mutations in a single DB transaction, a failure
 * mid-save now rolls back cleanly instead of leaving partial state.
 */
export const useFormEditorSave = ({
  parentType,
  initialData,
  customAttributeSchema,
  formFieldConfigurations,
}: UseFormEditorSaveOptions): ((output: FormEditorOutput) => Promise<void>) => {
  const evictFormCaches = (cache: ApolloCache<unknown>) => {
    evictField(cache, 'form_configuration');
    evictField(cache, 'form_field_configuration');
    evictField(cache, 'form_field_ordering');
    evictField(cache, 'form_section_configuration');
    evictField(cache, 'form_section_field');
    evictField(cache, 'form_section_ordering');
    evictField(cache, 'custom_attribute_schema');
  };

  const [saveFormConfiguration] = useMutation(SAVE_FORM_CONFIGURATION, {
    update: evictFormCaches,
  });

  return useCallback(
    async (output: FormEditorOutput) => {
      const initialFieldIds = new Set(Object.keys(initialData.fields));
      const outputFieldIds = new Set(Object.keys(output.fields));

      // ---- 1. Compute schema delta + new-field IDs (in-memory) ----
      let schema: JsonSchema7 = customAttributeSchema?.Schema ?? {};
      let uiSchema: VerticalLayout = customAttributeSchema?.UiSchema ?? {
        type: 'VerticalLayout',
        elements: [],
      };

      // Custom fields removed in this save.  Stored in server format because
      // form_field_configuration uses CustomAttributeData.{path} ids.
      const customFieldsToDelete: string[] = [];

      for (const id of initialFieldIds) {
        if (!outputFieldIds.has(id) && initialData.fields[id].isCustom) {
          const path = scopeToServerId(id).slice('CustomAttributeData.'.length);
          ({ Schema: schema, UiSchema: uiSchema } = removeField(path, {
            Schema: schema,
            UiSchema: uiSchema,
          }));
          customFieldsToDelete.push(scopeToServerId(id));
        }
      }

      // Map of editor temp IDs -> real scope-format field IDs assigned
      // client-side.  Used to rewrite section layout references.
      const tempIdToRealId = new Map<string, string>();
      let newFieldIndex = 0;
      for (const id of outputFieldIds) {
        if (initialFieldIds.has(id)) {
          continue;
        }
        const f = output.fields[id];
        const rawType = f.config?.fieldType ?? f.type;
        if (!isCustomAttributeFieldType(rawType)) {
          throw new Error(`Invalid custom attribute field type: ${rawType}`);
        }
        const fieldType: CustomAttributeFieldType = rawType;
        const attributeName = generateAttributeName(fieldType, newFieldIndex++);
        ({ Schema: schema, UiSchema: uiSchema } =
          addFieldToCustomAttributeSchema({
            data: {
              IsCustomField: true,
              Label: f.config?.fieldName ?? f.name,
              Description: f.config?.guidanceEnabled
                ? (f.config.guidance ?? null)
                : null,
              Options: mapEditorOptionsToFieldOptions(f.config?.options),
            },
            attributeName,
            type: fieldType,
            customAttributeSchema: { Schema: schema, UiSchema: uiSchema },
          }));
        tempIdToRealId.set(id, `#/properties/${attributeName}`);
      }

      const resolveId = (id: string): string => tempIdToRealId.get(id) ?? id;

      // ---- 2. Compute Conditions cleanup for remaining fields ----
      // When a referenced field is deleted, prune any token references in the
      // remaining fields' Conditions so saved logic doesn't dangle.  Mirrors
      // the orchestrator's deleteFieldAndPersist behaviour.
      const updatedConditionsByFieldId = new Map<
        string,
        TypedFormFieldConfiguration['Conditions']
      >();
      let remainingConfigs: TypedFormFieldConfiguration[] =
        formFieldConfigurations ?? [];
      for (const deletedFieldId of customFieldsToDelete) {
        const modified = removeFieldFromConditions({
          deletedFieldId,
          formFieldConfiguration: remainingConfigs,
        });
        for (const m of modified) {
          if (m.FieldId) {
            updatedConditionsByFieldId.set(m.FieldId, m.Conditions);
          }
        }
        // Apply the changes so subsequent deletions see the updated state.
        remainingConfigs = remainingConfigs.map((c) => {
          if (!c.FieldId) {
            return c;
          }
          const updated = modified.find((m) => m.FieldId === c.FieldId);

          return updated ? { ...c, Conditions: updated.Conditions ?? null } : c;
        });
      }

      // ---- 3. Build mutation variables ----
      const allFieldIds = output.sections.flatMap((s) =>
        s.fieldIds.map(resolveId)
      );

      // Build field configuration upserts for every field, including newly
      // inserted custom fields (whose form_field_configuration row no longer
      // exists from a separate action call — we create it here in the same
      // transaction).
      const fieldConfigurations = [...outputFieldIds].map((id) => {
        const f = output.fields[id];
        const fieldId = scopeToServerId(resolveId(id));
        const config: Record<string, unknown> = {
          FormConfigurationParentType: parentType,
          FieldId: fieldId,
          Required: f.required,
          // Preserve existing Hidden state. Hardcoding false would silently
          // unhide any field an admin had previously hidden.
          Hidden: f.hidden ?? false,
          ReadOnly: f.readOnly ?? false,
          Label: f.name || null,
          Description: f.config?.guidanceEnabled
            ? (f.config.guidance ?? null)
            : null,
        };
        // If conditions were updated by a delete cascade above, persist them.
        if (updatedConditionsByFieldId.has(fieldId)) {
          config.Conditions = updatedConditionsByFieldId.get(fieldId) ?? null;
        }

        return config;
      });

      // FormConfigurationParentType is filled in by Hasura from the parent
      // form_configuration row's ParentType via the fields_ordering array
      // relationship, so it must NOT be set explicitly here.
      const fieldOrderings = allFieldIds.map((fieldId, i) => ({
        FieldId: fieldId,
        Position: i,
      }));

      // Whether the schema needs to be touched at all.  If we're not adding
      // or removing any custom fields, leave custom_attribute_schema alone.
      const hasSchemaChanges =
        tempIdToRealId.size > 0 || customFieldsToDelete.length > 0;

      // Resolve the schema id: existing if there is one; otherwise generate a
      // new one when we have new custom fields (so we have something to link).
      const schemaId =
        customAttributeSchema?.Id ??
        (tempIdToRealId.size > 0 ? crypto.randomUUID() : null);

      const customAttributeSchemas =
        hasSchemaChanges && schemaId
          ? [{ Id: schemaId, Schema: schema, UiSchema: uiSchema }]
          : [];

      // form_configuration object: includes the schema link if we have one,
      // and nests field ordering so it's created atomically with the row.
      const formConfigurationObject: Record<string, unknown> = {
        ParentType: parentType,
        fields_ordering: {
          data: fieldOrderings,
          on_conflict: {
            update_columns: ['Position'],
            constraint: 'form_field_configuration_pkey',
          },
        },
      };
      if (schemaId) {
        formConfigurationObject.CustomAttributeSchemaId = schemaId;
      }

      await saveFormConfiguration({
        variables: {
          parentType,
          formConfigurationObject,
          fieldConfigurations,
          sectionConfig: output.sections.map((s) => ({
            FormConfigurationParentType: parentType,
            SectionId: s.id,
            Label: s.name,
            Description: s.config?.description ?? '',
          })),
          sectionOrdering: output.sections.map((s, i) => ({
            FormConfigurationParentType: parentType,
            SectionId: s.id,
            Position: i,
          })),
          fieldMappings: output.sections.flatMap((s) =>
            s.fieldIds.map((fieldId) => ({
              FormConfigurationParentType: parentType,
              SectionId: s.id,
              FieldId: resolveId(fieldId),
            }))
          ),
          customAttributeSchemas,
          fieldIds: allFieldIds,
          sectionIds: output.sections.map((s) => s.id),
          customFieldsToDelete,
        },
      });
    },
    [
      initialData,
      parentType,
      customAttributeSchema,
      formFieldConfigurations,
      saveFormConfiguration,
    ]
  );
};
